import { useEffect, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { apiFetch } from '../../services/api-client';
import { setCardAutoStamp, cardLinkErrorCopy } from '../../services/card-link-service';

// Customer-facing control for passive card auto-stamping (migration 048 /
// privacy policy §3, "Your card, indirectly"). Self-contained like
// PlayerName: fetches its own profile-get slice and posts through
// card-link-service rather than duplicating the API contract here.
//
// The checkbox is bound directly to "enabled" (not the server's inverted
// optOut) so ON always means checked — flipping the sense in the markup is
// how these toggles quietly end up backwards.
export default function CardLinkSettings() {
  const [optOut, setOptOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    apiFetch<{ cardLinkOptOut: boolean }>('/api/account', { action: 'profile-get' })
      .then((r) => { if (!dead) setOptOut(r.cardLinkOptOut); })
      .catch(() => { /* default (enabled) matches the server's default */ })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  async function handleToggle(nextEnabled: boolean) {
    if (busy) return;
    const nextOptOut = !nextEnabled;
    const previous = optOut;
    setBusy(true);
    setError(null);
    setConfirmation(null);
    setOptOut(nextOptOut); // optimistic; reverted below on failure
    try {
      const result = await setCardAutoStamp(nextOptOut);
      setOptOut(result.optOut);
      if (nextOptOut && result.linksForgotten > 0) {
        const n = result.linksForgotten;
        setConfirmation(`Forgotten. We've deleted ${n} saved card link${n === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      setOptOut(previous);
      setError(cardLinkErrorCopy(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div
        className="h-16 rounded-[8px] bg-background animate-pulse"
        data-testid="card-link-settings-loading"
      />
    );
  }

  const enabled = !optOut;

  // Renders inside MorePanel's own white card — no chrome of its own, same
  // always-on section-label language as ActivityLog above it.
  return (
    <div>
      <h3 id="card-link-heading" className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey mb-3 flex items-center gap-2">
        <CreditCard size={14} />
        Automatic stamps from your card
      </h3>
      <p className="font-grotesk font-medium text-[13px] text-body-grey mb-4">
        Once a stamp is matched to a card payment, we can recognise that same card next time so you
        earn a stamp automatically — no tapping needed. We never see or store your card number.
        Turning this off deletes every card we've already linked, right away.
      </p>

      <label htmlFor="card-link-toggle" className="flex items-center gap-3 cursor-pointer select-none">
        <input
          id="card-link-toggle"
          type="checkbox"
          checked={enabled}
          disabled={busy}
          aria-labelledby="card-link-heading card-link-status"
          onChange={(e) => handleToggle(e.target.checked)}
          className="w-5 h-5 accent-coral disabled:opacity-50"
        />
        <span
          id="card-link-status"
          className={`font-grotesk font-bold text-[13px] ${enabled ? 'text-coral' : 'text-body-grey'}`}
        >
          {enabled ? 'On — cards are remembered' : 'Off — nothing is remembered'}
        </span>
      </label>

      {/* aria-live announces save/confirm/error without a visible layout jump */}
      <div aria-live="polite" className="mt-2 min-h-[1.25rem]">
        {busy && <p className="font-grotesk text-[12px] text-body-grey">Saving…</p>}
        {!busy && confirmation && <p className="font-grotesk text-[12px] text-success">{confirmation}</p>}
        {!busy && error && <p className="font-grotesk text-[12px] text-error">{error}</p>}
      </div>
    </div>
  );
}
